import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import ExpandableSection from '../../expandableSection';
import { ActionButtons } from './sectionShared';
import AddTbhResourceModal from './addTbhResourceModal';
import UseAxios from '../../../../../Api/http/http';
import { deleteTbhResourceAllocation } from '../../../../../Api/request/projectDetails';
import { DeleteConfirmationModal } from '../../expandableSectionsModals';
import { useSnackbar } from '../../../../../Components/Snackbar/snackBarProvider';

const formatDateToDMY = (dateVal) => {
  if (!dateVal || dateVal === '-') return '-';
  const parsedDate = dayjs(dateVal);
  if (!parsedDate.isValid()) return String(dateVal);
  return parsedDate.format('D-MMM-YYYY');
};

const formatNumberTo2Dp = (value) => Number(value || 0).toFixed(2);

const TbhResourcesSection = ({
  expanded,
  onToggle,
  onDownload,
  onMoreMenuItems,
  showMoreAction,
  tbhResources,
  isAddTbhResourceOpen,
  onCloseAddTbhResource,
  projectId,
  projectStatus,
  onRefreshData,
  onEdit,
  allocationData,
  canUpdate,
  canDelete,
  isLoading,
}) => {
  const { snackbarOpen } = useSnackbar();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingRow, setDeletingRow] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const tbhTotals = useMemo(() => (
    (Array.isArray(tbhResources) ? tbhResources : []).reduce((acc, row) => {
      acc.days += Number(row?.allocatedDays || 0);
      acc.hours += Number(row?.allocatedHours || 0);
      return acc;
    }, { days: 0, hours: 0 })
  ), [tbhResources]);

  const handleDeleteClick = (row) => {
    setDeletingRow(row);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    const tbhId = deletingRow?.id ?? deletingRow?.tbhId;
    if (!tbhId) {
      snackbarOpen('Cannot delete: TBH ID is missing.', 'error');
      setDeleteOpen(false);
      setDeletingRow(null);
      return;
    }
    setIsDeleting(true);
    try {
      const response = await UseAxios(deleteTbhResourceAllocation(tbhId));
      if (response && response.success !== false) {
        if (typeof onRefreshData === 'function') {
          onRefreshData();
        }
        snackbarOpen('TBH Resource allocation deleted successfully.', 'success');
        setDeleteOpen(false);
        setDeletingRow(null);
      } else {
        snackbarOpen(response?.message || 'Failed to delete TBH resource allocation', 'error');
      }
    } catch (err) {
      snackbarOpen(
        err?.response?.data?.message || err?.message || 'Failed to delete TBH resource allocation',
        'error',
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {isAddTbhResourceOpen && (
        <AddTbhResourceModal
          onClose={onCloseAddTbhResource}
          projectId={projectId}
          projectStatus={projectStatus}
          onRefreshData={onRefreshData}
          allocationData={allocationData}
        />
      )}

      <DeleteConfirmationModal
        isOpen={deleteOpen}
        title="Delete TBH Resource Allocation"
        message="Are you sure you want to delete this TBH resource allocation?"
        onClose={() => {
          setDeleteOpen(false);
          setDeletingRow(null);
        }}
        onConfirm={handleConfirmDelete}
        isSubmitting={isDeleting}
      />

      <ExpandableSection
        title="TBH Resources"
        sectionId="project-details-section-tbh-resources"
        expanded={expanded}
        onToggle={onToggle}
        onDownload={onDownload}
        onMoreMenuItems={onMoreMenuItems}
        showMoreAction={showMoreAction}
        isLoading={isLoading}
      >
        <div className="section-content tbh-resources-section">
          <div className="progress-table-wrapper">
            <div className="progress-table-scroll">
              <table
                className="progress-data-table tbh-resources-table"
                style={{ minWidth: '1940px', tableLayout: 'fixed' }}
              >
                <thead>
                  <tr>
                    <th className="th-cell" style={{ width: '50px' }}>Sl</th>
                    <th className="th-cell" style={{ width: '100px' }}>TBH Id</th>
                    <th className="th-cell" style={{ width: '150px' }}>Employee Vertical</th>
                    <th className="th-cell" style={{ width: '120px' }}>Employee Region</th>
                    <th className="th-cell" style={{ width: '120px' }}>Employee Country</th>
                    <th className="th-cell" style={{ width: '160px' }}>Employee Role</th>
                    <th className="th-cell" style={{ width: '150px' }}>Employee Experience</th>
                    <th className="th-cell" style={{ width: '140px' }}>Employee Location</th>
                    <th className="th-cell" style={{ width: '160px' }}>Employee Department</th>
                    <th className="th-cell" style={{ width: '110px', textAlign: 'right' }}>Allocation%</th>
                    <th className="th-cell" style={{ width: '110px' }}>Start Date</th>
                    <th className="th-cell" style={{ width: '110px' }}>End Date</th>
                    <th className="th-cell th-numeric" style={{ width: '110px' }}>
                      <div className="th-content th-content-numeric">
                        <span>Allocated Days</span>
                      </div>
                      <div className="header-total">
                        {Number(tbhTotals.days || 0).toFixed(2)}
                      </div>
                    </th>
                    <th className="th-cell th-numeric" style={{ width: '110px' }}>
                      <div className="th-content th-content-numeric">
                        <span>Allocated Hours</span>
                      </div>
                      <div className="header-total">
                        {Number(tbhTotals.hours || 0).toFixed(2)}
                      </div>
                    </th>
                    <th className="th-cell" style={{ width: '150px' }}>Remarks</th>
                    <th className="th-cell" style={{ width: '100px' }}>Scrum</th>
                    <th className="th-cell" style={{ width: '90px' }}>Is Active</th>
                    <th
                      className="th-cell tbh-action-col"
                      style={{ width: '70px' }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tbhResources.length > 0 ? (
                    tbhResources.map((row, idx) => (
                      <tr key={row.id || idx} className="tr-data">
                        <td className="td-cell" style={{ textAlign: 'center' }}>{idx + 1}</td>
                        <td className="td-cell" style={{ fontWeight: '500' }}>{row.tbhId || '-'}</td>
                        <td className="td-cell">{row.vertical || '-'}</td>
                        <td className="td-cell">{row.regionName || '-'}</td>
                        <td className="td-cell">{row.countryName || '-'}</td>
                        <td className="td-cell" style={{ fontWeight: '500' }}>{row.roleName || '-'}</td>
                        <td className="td-cell">{row.experience || '-'}</td>
                        <td className="td-cell">{row.location || '-'}</td>
                        <td className="td-cell">{row.department || '-'}</td>
                        <td className="td-cell" style={{ textAlign: 'right', fontWeight: '500' }}>
                          {Number(row.allocationPct || 0).toFixed(2)}%
                        </td>
                        <td className="td-cell">{formatDateToDMY(row.startDate)}</td>
                        <td className="td-cell">{formatDateToDMY(row.endDate)}</td>
                        <td className="td-cell" style={{ textAlign: 'right', fontWeight: '500' }}>
                          {formatNumberTo2Dp(row.allocatedDays)}
                        </td>
                        <td className="td-cell" style={{ textAlign: 'right', fontWeight: '500' }}>
                          {formatNumberTo2Dp(row.allocatedHours)}
                        </td>
                        <td className="td-cell">{row.remark || '-'}</td>
                        <td className="td-cell">{row.scrumTeamName || '-'}</td>
                        <td className="td-cell">
                          <span
                            className={
                              `status-badge ${row.isActive === 1 || row.isActive === true
                                ? 'active'
                                : 'moved-out'}`
                            }
                          >
                            {row.isActive === 1 || row.isActive === true ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="td-cell tbh-action-col" style={{ textAlign: 'center' }}>
                          <ActionButtons
                            onEdit={canUpdate ? () => onEdit(row) : undefined}
                            onDelete={canDelete ? () => handleDeleteClick(row) : undefined}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className="tr-empty">
                      <td
                        colSpan="18"
                        className="td-cell tbh-empty-cell"
                      >
                        No TBH resources details available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </ExpandableSection>
    </>
  );
};

TbhResourcesSection.propTypes = {
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onDownload: PropTypes.func,
  onMoreMenuItems: PropTypes.array,
  showMoreAction: PropTypes.bool,
  tbhResources: PropTypes.array,
  isAddTbhResourceOpen: PropTypes.bool,
  onCloseAddTbhResource: PropTypes.func,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  projectStatus: PropTypes.string,
  onRefreshData: PropTypes.func,
  onEdit: PropTypes.func,
  allocationData: PropTypes.object,
  canUpdate: PropTypes.bool,
  canDelete: PropTypes.bool,
  isLoading: PropTypes.bool,
};

TbhResourcesSection.defaultProps = {
  onDownload: null,
  onMoreMenuItems: [],
  showMoreAction: false,
  tbhResources: [],
  isAddTbhResourceOpen: false,
  onCloseAddTbhResource: () => {},
  projectId: null,
  projectStatus: '',
  onRefreshData: () => {},
  onEdit: () => {},
  allocationData: null,
  canUpdate: false,
  canDelete: false,
  isLoading: false,
};

export default TbhResourcesSection;

